import conn from "@/services/ApiConnection"
import i18n from '@/lang/i18n';
import Response from "@/types/responses/getWorkload"
import WorkloadType from "@/types/Workload"
import DeliveryPlanWork from "@/types/DeliveryPlanWork"
import Sha256 from "@/services/models/Sha256"
import {
  formatDateTimeWithSeconds,
  getStringLength
} from "@/services/functions"

const NAME_MAX_WIDTH_REFUELING = 30
const NAME_MAX_WIDTH_LOADING_UNLOADING = 20
const NOTE_MAX_WIDTH = 40

class Workload {
  public static async get(operationOfficeId: number, work_id: string): Promise<Response> {
    const res = await conn.get(`/api/v1/operation_offices/${operationOfficeId}/workloads/${work_id}`)
    return new Promise((resolve) => {
      const data = res?.data || []
      resolve(data)
    })
  }

  public static async upsert(operationOfficeId: number, work_id: string, workItem: DeliveryPlanWork, items: WorkloadType[], deleteItems: number[]): Promise<Response> {
    const res = await conn.put(`/api/v1/operation_offices/${operationOfficeId}/workloads/${work_id}`, {
      delivery_plan_work: workItem,
      workloads: items,
      delete_workload_ids: deleteItems,
    })
    return new Promise((resolve) => {
      const data = res?.data || []
      resolve(data)
    })
  }

  public static generateWorkId(delivery_plan_id: string, work: string, status: boolean, date_time: string) {
    const trimd_date_time = formatDateTimeWithSeconds(new Date(date_time))
    const str_status = status ? "true" : "false"
    const __concated = delivery_plan_id + work + str_status + trimd_date_time;
    return Sha256.generate(__concated)
  }

  public static validate(work: string, items: WorkloadType[]): any[] {
    const error_messages: any = []

    items.map((item, index) => {
      const isValidateName = this.validateName(item['name'], work)
      const isValidateQuantity = this.validateQuantity(item['quantity'], work)
      const isValidateUnit = this.validateUnit(item['unit'])
      const isValidateNote = this.validateNote(item['note'])
      if (isValidateName) {
        error_messages['workloads.' + index + '.name'] = isValidateName
      }
      if (isValidateQuantity) {
        error_messages['workloads.' + index + '.quantity'] = isValidateQuantity
      }
      if (isValidateUnit) {
        error_messages['workloads.' + index + '.unit'] = isValidateUnit
      }
      if (isValidateNote) {
        error_messages['workloads.' + index + '.note'] = isValidateNote
      }
    })
    return error_messages
  }

  public static validateName(item: string | null, work: string): string {
    if (!item) {
      return i18n.global.t('pages.workload.validate.required')
    }

    const stringLength = getStringLength(item);
    if (work === 'refueling' && stringLength > NAME_MAX_WIDTH_REFUELING) {
      return i18n.global.t('pages.workload.validate.max30BytesTextRequired')
    }
    if ((work === 'loading' || work === 'unloading') && stringLength > NAME_MAX_WIDTH_LOADING_UNLOADING) {
      return i18n.global.t('pages.workload.validate.max20BytesTextRequired')
    }
    return ""
  }

  public static validateQuantity(item: number | null, work: string): string {
    if (item !== 0 && !item) {
      return i18n.global.t('pages.workload.validate.required')
    }

    if (work == "refueling" && this.validateQuantityRefueling(item)) {
      return this.validateQuantityRefueling(item)
    } else if (this.validateQuantityLoadingAndUnloading(item)) {
      return this.validateQuantityLoadingAndUnloading(item)
    }

    return ""
  }

  public static validateNote(item: string | null): string {
    if (item && getStringLength(item) > NOTE_MAX_WIDTH) {
      return i18n.global.t('pages.workload.validate.max40BytesTextRequired')
    }
    return ""
  }

  public static validateUnit(item: string | null): string {
    if (!item) {
      return i18n.global.t('pages.workload.validate.required')
    }
    if (item && item.length > 5) {
      return i18n.global.t('pages.workload.validate.max5TextRequired')
    }
    return ""
  }

  public static validateQuantityRefueling(item: number): string {
    if (!this.isNumber(item)) {
      return i18n.global.t('pages.workload.validate.QuantityRefuelingNumber')
    }
    if (item < 0 || 1000 <= item) {
      return i18n.global.t('pages.workload.validate.QuantityRefuelingNumber')
    }
    if (String(item).includes('.') && String(item).split(".")[1].length > 2) {
      return i18n.global.t('pages.workload.validate.QuantityRefuelingDecimal')
    }

    return ""
  }

  public static validateQuantityLoadingAndUnloading(item: number): string {
    if (!this.isNumber(item)) {
      return i18n.global.t('pages.workload.validate.QuantityLoadingAndUnloadingNumber')
    }
    if (item < 0 || 100000 <= item) {
      return i18n.global.t('pages.workload.validate.QuantityLoadingAndUnloadingNumber')
    }
    if (String(item).includes('.') && String(item).split(".")[1].length > 4) {
      return i18n.global.t('pages.workload.validate.QuantityLoadingAndUnloadingDecimal')
    }

    return ""
  }

  public static isNumber(item: any): boolean {
    return /^([1-9]\d*|0)(\.\d+)?$/.test(item.toString())
  }
}

export default Workload
